import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/login.css";
import { signInUser } from "../api/authService";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { getData } from "../api/ApiServices";
import { useDispatch, useSelector } from "react-redux";
import { setCompanyDetails } from "../redux/slices/companySlice";
import { setMenus } from "../redux/slices/permissionSlice";
import { setUserInfo } from "../redux/userSlice";
import { RootState } from "../redux/slices/store";

const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errors[e.target.name as keyof typeof errors]) {
      setErrors({ ...errors, [e.target.name]: undefined });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    let isValid = true;

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };
  
  const permissions = useSelector((state:RootState) => state.permissions);

  useEffect(() => {
    console.log("Updated permissions: ", permissions);
  }, [permissions]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setErrors({});
    setLoading(true);

    const response: any = await signInUser(formData.email, formData.password);
    console.log(response);

    if (response.data) {
      dispatch(setUserInfo({
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.email
      }));
      const menus = response.data.roleData.menus;
      console.log(menus);
      dispatch(setMenus(menus));
      
      console.log("permissions: ", permissions);
    }

    if (response?.error) {
      setErrors({ general: response.error });
    } else if (response?.data.token) {
      localStorage.setItem("authToken", response.data.token);
      getCompanyDetails();
      navigate("/dashboard");
    } else {
      setErrors({ general: "An unknown error occurred. Please try again." });
    }

    setLoading(false);
  };
  
  const getCompanyDetails = async () => {
    const response: any = await getData("/company-profile", {});
    console.log(response.data.data);
    dispatch(setCompanyDetails(response.data.data));
  };

  return (
    <div className="main-container-for-login">
      <div className="left-container-for-login">
        <img
          src="/images/login_page_intro_image.png"
          alt=""
          className="login-static-logo-for-login"
        />
      </div>

      <div className="right-container-for-login">
        <img
          src="/images/app_logo.png"
          alt=""
          className="login-app-logo-for-login"
        />
        <p className="login-title-for-login">AGAM HIVE</p>
        <p className="login-subtitle-for-login">Manage Your Inventory</p>
        <p className="login-welcome-for-login">Welcome, Back</p>
        <p className="login-plsLogin-for-login">Please log in to continue.</p>

        <div className="login-form-for-login">
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formBasicEmail">
              <Form.Label>
                Email address <span className="red-asterisk-for-login">*</span>
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter your email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <div
                  style={{ color: "red", fontSize: "14px", marginTop: "5px" }}
                >
                  {errors.email}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBasicPassword">
              <Form.Label>
                Password <span className="red-asterisk-for-login">*</span>
              </Form.Label>
              <div
                className="password-wrapper"
                style={{ position: "relative" }}
              >
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your Password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <span
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                >
                  {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </span>
              </div>
              {errors.password && (
                <div
                  style={{ color: "red", fontSize: "14px", marginTop: "5px" }}
                >
                  {errors.password}
                </div>
              )}
            </Form.Group>

            {errors.general && (
              <p
                style={{
                  color: "red",
                  textAlign: "center",
                  marginBottom: "10px",
                }}
              >
                {errors.general}
              </p>
            )}

            <Button
              variant="primary"
              type="submit"
              className="login-submit-button-for-login"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                    style={{ color: "white" }}
                  ></span>
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </Button>
          </Form>
        </div>
        <p className="text-center" style={{ marginTop: "10px" }}>
          Don't have an account?{" "}
          <span
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "16px",
              color: "#FF7A01",
            }}
          >
            Register now
          </span>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;